// Hand-rolled DI: a Token<T> phantom-types its resolved value; the container is just a Map.
// No decorators, no metadata reflection, no third-party.

declare const TOKEN_BRAND: unique symbol;
export type Token<T> = { readonly key: symbol; readonly [TOKEN_BRAND]?: T };

export function token<T>(name: string): Token<T> {
  return { key: Symbol(name) };
}

type Resolver<T> = (c: Container) => T;

export class Container {
  private readonly singletons = new Map<symbol, unknown>();
  private readonly resolvers = new Map<symbol, Resolver<unknown>>();
  private readonly factories = new Map<symbol, Resolver<unknown>>();
  private readonly resolving = new Set<symbol>();

  registerSingleton<T>(tok: Token<T>, factory: Resolver<T>): void {
    this.resolvers.set(tok.key, factory as Resolver<unknown>);
  }

  registerTransient<T>(tok: Token<T>, factory: Resolver<T>): void {
    this.factories.set(tok.key, factory as Resolver<unknown>);
  }

  resolve<T>(tok: Token<T>): T {
    if (this.singletons.has(tok.key)) return this.singletons.get(tok.key) as T;

    const factory = this.resolvers.get(tok.key) ?? this.factories.get(tok.key);
    if (factory === undefined) {
      throw new Error(`Container: token not registered: ${String(tok.key.description)}`);
    }

    // The DAG rule (INVARIANTS rule 7) needs an enforcer or it is only a wish: without this, a
    // cycle in di.ts recurses until the stack blows and the message names no token.
    if (this.resolving.has(tok.key)) {
      throw new Error(`Circular dependency detected resolving ${String(tok.key.description)}`);
    }
    this.resolving.add(tok.key);
    try {
      const instance = factory(this) as T;
      if (this.resolvers.has(tok.key)) this.singletons.set(tok.key, instance);
      return instance;
    } finally {
      this.resolving.delete(tok.key);
    }
  }

  // For testing: drop every registration and cached instance. Production never calls this; the
  // container is configured once by ud_di() at boot.
  reset(): void {
    this.singletons.clear();
    this.resolvers.clear();
    this.factories.clear();
    this.resolving.clear();
  }
}

export const root = new Container();
